import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/pagination";
import { OrgEditDialog, type OrgItem } from "@/components/org-edit-dialog";
import { trpc } from "@/trpc/trpc";
import { Plus, Users } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/orgs/")({
  component: AdminOrgsPage,
});

function AdminOrgsPage() {
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OrgItem | null>(null);

  const { data, isLoading } = trpc.admin.listAdminOrgs.useQuery({
    page,
    limit: 20,
  });

  const totalPages = Math.ceil((data?.total ?? 0) / 20);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Organizacje</h1>
          <p className="text-sm text-muted-foreground">
            {data?.total ?? 0} organizacji
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          <Plus className="mr-1 size-4" />
          Nowa organizacja
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Ładowanie...</p>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Nazwa</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Członkowie</th>
                  <th className="px-4 py-3 font-medium">Utworzona</th>
                  <th className="px-4 py-3 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((org) => (
                  <tr key={org.id} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">{org.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {org.contactEmail ?? "—"}
                    </td>
                    <td className="px-4 py-3">{org.memberCount}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {new Date(org.createdAt).toLocaleDateString("pl-PL")}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditing(org);
                            setDialogOpen(true);
                          }}
                        >
                          Edytuj
                        </Button>
                        <Link to="/admin/orgs/$id" params={{ id: org.id }}>
                          <Button variant="ghost" size="sm">
                            <Users className="size-4" />
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
                {data?.items.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      Brak organizacji
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 pb-3">
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </Card>
      )}

      <OrgEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        org={editing}
      />
    </div>
  );
}
